import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  Layout,
  ToastProvider,
  ThemeProvider,
  DensityProvider,
  useTranslationTyped,
} from '@kantera/ui';
import type { Density } from '@kantera/ui';
import { AuthProvider } from './context/AuthContext';
import { UserProfileProvider } from './context/UserProfileContext';
import { ProfileCompletionGate } from './components/ProfileCompletionGate';
import { PageFallback } from './components/PageFallback';
import { I18nProvider } from './context/I18nContext';
import { UploadProvider } from './context/UploadContext';
import { UploadIndicator } from './components/UploadIndicator';
import { EnvBanner, ENV_BANNER_HEIGHT } from './components/EnvBanner';
import { SystemStatusBanner } from './components/SystemStatusBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PLATFORM_ROLES } from './utils/userRoles';
import { PlayerSelfRoute } from './components/PlayerSelfRoute';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { ProfileViewProvider } from './context/ProfileViewContext';

// W-15 (2026-05-03): every route lazy-loaded so the index chunk only ships
// app shell + shared utilities + the bottom-of-the-funnel pages every user
// hits (Login, NotFound). Pre-W-15 the index chunk was 1.3MB raw / 348KB
// gzipped — well past Vite's 500KB warning and the snapshot's 1.5s LCP
// target on 4G. Each lazy() import becomes its own chunk that the browser
// fetches on first navigation only.
//
// NotFound stays eager because it's tiny (<3KB) and is reached from every
// invalid route — its Suspense fallback would flicker. Login also stays
// eager because it's the unauthenticated entry point.
import Login from './pages/Login';
import NotFound from './pages/NotFound';

const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const AcceptInviteCreateAccount = lazy(() => import('./pages/AcceptInviteCreateAccount'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const AuthConfirmed = lazy(() => import('./pages/AuthConfirmed'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
// Public marketing page — what `/` shows a visitor with no session (see RootRoute).
const Landing = lazy(() => import('./pages/Landing'));
const RefereeDashboard = lazy(() => import('./pages/RefereeDashboard'));
const PlayerCheckin = lazy(() => import('./pages/PlayerCheckin'));
const IncidentLog = lazy(() => import('./pages/IncidentLog'));
const TeamManagerDashboard = lazy(() => import('./pages/TeamManagerDashboard'));
const TeamManagerComms = lazy(() => import('./pages/TeamManagerComms'));
const TeamManagerVolunteers = lazy(() => import('./pages/TeamManagerVolunteers'));
const Players = lazy(() => import('./pages/Players'));
const PlayerDetail = lazy(() => import('./pages/PlayerDetail'));
const PlayerCreate = lazy(() => import('./pages/PlayerCreate'));
const Teams = lazy(() => import('./pages/Teams'));
const TeamDetail = lazy(() => import('./pages/TeamDetail'));
const TeamCreate = lazy(() => import('./pages/TeamCreate'));
const TeamComparison = lazy(() => import('./pages/TeamComparison'));
const Matches = lazy(() => import('./pages/Matches'));
const MatchCreate = lazy(() => import('./pages/MatchCreate'));
const MatchDetail = lazy(() => import('./pages/MatchDetail'));
const MatchShareCard = lazy(() => import('./pages/MatchShareCard'));
const Clubs = lazy(() => import('./pages/Clubs'));
const ClubDetail = lazy(() => import('./pages/ClubDetail'));
const ClubManagement = lazy(() => import('./pages/ClubManagement'));
const Settings = lazy(() => import('./pages/Settings'));

const Seasons = lazy(() => import('./pages/Seasons'));
const Leagues = lazy(() => import('./pages/Leagues'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const TrainingSessions = lazy(() => import('./pages/TrainingSessions'));
const TrainingSessionDetail = lazy(() => import('./pages/TrainingSessionDetail'));
const LeagueDetail = lazy(() => import('./pages/LeagueDetail'));
const ConsentManagement = lazy(() => import('./pages/ConsentManagement'));
const Standings = lazy(() => import('./pages/Standings'));
const Import = lazy(() => import('./pages/Import'));
const Formation = lazy(() => import('./pages/Formation'));

// Heavy pages — code-split to defer Recharts (~300 KB) and large component trees
// until the user actually navigates to these routes.
const Analytics = lazy(() => import('./pages/Analytics'));
const Gamification = lazy(() => import('./pages/Gamification'));
const Billing = lazy(() => import('./pages/Billing'));
const Compliance = lazy(() => import('./pages/Compliance'));
// KAN-221: a club admin deletes a withdrawn child's media without viewing it.
const WithdrawnMedia = lazy(() => import('./pages/WithdrawnMedia'));

// New pages — lazy-loaded (not yet built; other agents are building these in parallel)
const Messaging = lazy(() => import('./pages/Messaging'));
const VideoAnalysis = lazy(() => import('./pages/VideoAnalysis'));
const Highlights = lazy(() => import('./pages/Highlights'));
const PhotoGallery = lazy(() => import('./pages/PhotoGallery'));
const SkillAssessments = lazy(() => import('./pages/SkillAssessments'));
const TrainingPlans = lazy(() => import('./pages/TrainingPlans'));
const PlayerDevelopment = lazy(() => import('./pages/PlayerDevelopment'));
const Reports = lazy(() => import('./pages/Reports'));
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const NutritionHub = lazy(() => import('./pages/NutritionHub'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const ParentChildManagement = lazy(() => import('./pages/ParentChildManagement'));
const FamilyCircle = lazy(() => import('./pages/FamilyCircle'));
const FamilyDashboard = lazy(() => import('./pages/FamilyDashboard'));
const FamilySchedule = lazy(() => import('./pages/FamilySchedule'));
const FamilyStandings = lazy(() => import('./pages/FamilyStandings'));
const FamilyBadges = lazy(() => import('./pages/FamilyBadges'));
const AcceptGuardianInvite = lazy(() => import('./pages/AcceptGuardianInvite'));
const SmsUnsubscribe = lazy(() => import('./pages/SmsUnsubscribe'));
const EmailUnsubscribe = lazy(() => import('./pages/EmailUnsubscribe'));
const RsvpFromLink = lazy(() => import('./pages/RsvpFromLink'));
const SmsOptIn = lazy(() => import('./pages/SmsOptIn'));
const BulkAdd = lazy(() => import('./pages/BulkAdd'));
const PracticeKiosk = lazy(() => import('./pages/PracticeKiosk'));
const PracticeKioskOthers = lazy(() => import('./pages/PracticeKioskOthers'));
const CoachChannel = lazy(() => import('./pages/CoachChannel'));
const StaffManagement = lazy(() => import('./pages/StaffManagement'));
const EmailDeliveries = lazy(() => import('./pages/EmailDeliveries'));
// KAN-202: families' erasure requests — admins act on them within charter Rule 5's 72 hours.
const ErasureRequests = lazy(() => import('./pages/ErasureRequests'));
const Venues = lazy(() => import('./pages/Venues'));

// Wave 2: Match day operations
const LineupBuilder = lazy(() => import('./pages/LineupBuilder'));
const LiveScoring = lazy(() => import('./pages/LiveScoring'));
const PostMatchReport = lazy(() => import('./pages/PostMatchReport'));

// Wave 3: Parent portal
const MatchRsvps = lazy(() => import('./pages/MatchRsvps'));
const CarpoolCoordinator = lazy(() => import('./pages/CarpoolCoordinator'));
const ParentDevelopmentReport = lazy(() => import('./pages/ParentDevelopmentReport'));

// Tactical Animation Engine — lazy-loaded (animation-heavy, code-split from main bundle)
const TacticsLibrary = lazy(() => import('./pages/TacticsLibrary'));
const FormationEncyclopedia = lazy(() => import('./pages/FormationEncyclopedia'));

// Club onboarding wizard
const ClubOnboarding = lazy(() => import('./pages/ClubOnboarding'));
const OnboardingClubBrand = lazy(() => import('./pages/OnboardingClubBrand'));
const OnboardingCoach = lazy(() => import('./pages/OnboardingCoach'));
const OnboardingPlayer = lazy(() => import('./pages/OnboardingPlayer'));
const OnboardingParent = lazy(() => import('./pages/OnboardingParent'));
const SetupChooser = lazy(() => import('./pages/SetupChooser'));
const CreateClub = lazy(() => import('./pages/CreateClub'));
const GuidedSetup = lazy(() => import('./pages/GuidedSetup'));

// KI-012 B6 — League Scheduling
const LeagueScheduling = lazy(() => import('./pages/LeagueScheduling'));
// KI-012 B2 — Avatar Studio (coach/admin/parent)
const AvatarStudio = lazy(() => import('./pages/AvatarStudio'));

// Wave 6: Player portal + gamification
const MyProfile = lazy(() => import('./pages/PlayerPortal/MyProfile'));
const MyAvatarPage = lazy(() => import('./pages/PlayerPortal/MyAvatarPage'));
const MySchedule = lazy(() => import('./pages/PlayerPortal/MySchedule'));
const MyTeam = lazy(() => import('./pages/PlayerPortal/MyTeam'));
const Achievements = lazy(() => import('./pages/PlayerPortal/MyAchievements'));
const MyHomework = lazy(() => import('./pages/PlayerPortal/MyHomework'));
const SkillChallenges = lazy(() => import('./pages/PlayerPortal/MyChallenges'));

// Wave 7: Communication, Registration & Settings
const DirectMessages = lazy(() => import('./pages/DirectMessages'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const RegistrationFormBuilder = lazy(() => import('./pages/RegistrationFormBuilder'));
const RegistrationManagement = lazy(() => import('./pages/RegistrationManagement'));
const AppearanceSettings = lazy(() => import('./pages/AppearanceSettings'));

// Wave 8: Tournaments, Season Archive, Search
const TournamentManager = lazy(() => import('./pages/TournamentManager'));
const SeasonArchive = lazy(() => import('./pages/SeasonArchive'));
const SeasonDetail = lazy(() => import('./pages/SeasonDetail'));
const SearchPage = lazy(() => import('./pages/Search'));

// Wave 9: Tactical Animation Engine
const TacticsEditor = lazy(() => import('./pages/TacticsEditor'));
const GameDayPrep = lazy(() => import('./pages/GameDayPrep'));
const MatchFilm = lazy(() => import('./pages/MatchFilm'));

// Safeguarding — Track 0 competitive enhancement
const Safeguarding = lazy(() => import('./pages/Safeguarding'));

// Platform admin
const PlatformDashboard = lazy(() => import('./pages/PlatformDashboard'));
const FeatureFlags = lazy(() => import('./pages/FeatureFlags'));
const PlatformInviteClubAdmin = lazy(() => import('./pages/PlatformInviteClubAdmin'));
const PlatformWaitlist = lazy(() => import('./pages/PlatformWaitlist'));
const PlatformAddClub = lazy(() => import('./pages/PlatformAddClub'));
const ClubDeepDive = lazy(() => import('./pages/ClubDeepDive'));
const ProductAnalytics = lazy(() => import('./pages/ProductAnalytics'));
const EngineeringDashboard = lazy(() => import('./pages/EngineeringDashboard'));
const MarketingDashboard = lazy(() => import('./pages/MarketingDashboard'));
const Tryouts = lazy(() => import('./pages/Tryouts'));
const FinanceDashboard = lazy(() => import('./pages/FinanceDashboard'));
const SupportDashboard = lazy(() => import('./pages/SupportDashboard'));
const ContentManagement = lazy(() => import('./pages/ContentManagement'));
const CostModel = lazy(() => import('./pages/CostModel'));
const VideoReview = lazy(() => import('./pages/VideoReview'));

// KI-012 — AI Batch (B7 Scouting, B8 Game Strategy)
const ScoutingReports = lazy(() => import('./pages/ScoutingReports'));
const GameStrategy = lazy(() => import('./pages/GameStrategy'));
// D-004 Discovery (flag-gated, default OFF — see docs/FEATURE_FLAGS.md)
const DiscoveryFeed = lazy(() => import('./pages/DiscoveryFeed'));
const DiscoveryProfile = lazy(() => import('./pages/DiscoveryProfile'));
const DiscoveryControls = lazy(() => import('./pages/DiscoveryControls'));
// D-005 Comparison (flag-gated, default OFF — see docs/FEATURE_FLAGS.md)
const PlayerComparison = lazy(() => import('./pages/PlayerComparison'));
const SharedComparison = lazy(() => import('./pages/SharedComparison'));

// Shared Suspense fallback now lives in ./components/PageFallback (imported above)
// so ProfileCompletionGate's authenticated-routes boundary uses the same spinner.

// AppShell (components/AppShell.tsx) renders beside the routes: keyboard shortcuts, the help button,
// and the one-time hand-off of a brand-new owner to /setup (KAN-118 — it replaced two pop-up wizards).

/**
 * RootRoute — `/` is two different pages.
 *
 * A visitor with no session gets the marketing page (pages/Landing.tsx), with
 * no sidebar, no top bar, no ProtectedRoute bounce to /login. A signed-in user
 * gets the Dashboard inside the normal authenticated shell, exactly as before:
 * eleven places in the app `navigate('/')` meaning "go home", and Login itself
 * sends a signed-in user to `/`, so `/` must keep meaning Dashboard for them.
 *
 * The decision is made on the same fields ProtectedRoute uses — a resolved
 * user AND a resolved tenant with an org — so the two never disagree about who
 * is signed in. passwordRecovery is deliberately NOT consulted here: a user
 * mid-recovery still renders the shell, and ProtectedRoute inside it is what
 * sends them to /reset-password, as it does on every other route.
 */
const RootRoute: React.FC<{ signedIn: React.ReactNode }> = ({ signedIn }) => {
  const { user, tenant, loading } = useAuth();
  if (loading) return <PageFallback />;
  const hasSession = !!user && !!tenant && String(tenant.orgId ?? '').trim().length > 0;
  if (!hasSession) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Landing />
      </Suspense>
    );
  }
  return <>{signedIn}</>;
};

const AppRoutes: React.FC = () => {
  const { t } = useTranslationTyped();
  const layoutLabels = {
    skipToMain: t('appShell.skipToMain'),
    mainNavAria: t('appShell.mainNavAria'),
    collapseSidebarAria: t('appShell.collapseSidebarAria'),
    closeMenuAria: t('appShell.closeMenuAria'),
    openMenuAria: t('appShell.openMenuAria'),
  };
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<PageFallback />}>
            <ResetPassword />
          </Suspense>
        }
      />

      {/* KI-031 §1.6 — dedicated forgot-password route. */}
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageFallback />}>
            <ForgotPassword />
          </Suspense>
        }
      />

      {/* KI-031 §1.8 — accept invitation (token IS the credential; public). */}
      <Route
        path="/invite"
        element={
          <Suspense fallback={<PageFallback />}>
            <AcceptInvitation />
          </Suspense>
        }
      />

      {/* KI-031 §1.9 — create account from invitation token (public). */}
      <Route
        path="/invite/create-account"
        element={
          <Suspense fallback={<PageFallback />}>
            <AcceptInviteCreateAccount />
          </Suspense>
        }
      />

      {/* KI-031 §1.10 — complete profile after invitation accepted. */}
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageFallback />}>
              <CompleteProfile />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* New-owner club setup — full-screen (outside the dashboard Layout) so the
          onboarding matches the mockup authShell, not framed by sidebar/topbar. */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute requiredRole="admin">
            <Suspense fallback={<PageFallback />}>
              <SetupChooser />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* BN-048: SetupChooser has always offered "Build step by step" pointing here; the route
          did not exist, so the card was dead on the first screen a new owner sees. */}
      <Route
        path="/setup/build"
        element={
          <ProtectedRoute requiredRole="admin">
            <Suspense fallback={<PageFallback />}>
              <GuidedSetup />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/create"
        element={
          <ProtectedRoute requiredRole="admin">
            <Suspense fallback={<PageFallback />}>
              <CreateClub />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/verify-email"
        element={
          <Suspense fallback={<PageFallback />}>
            <EmailVerification />
          </Suspense>
        }
      />

      {/* Where the server-side signUp's confirmation email lands (public; Supabase
          has already verified the token on its own host before redirecting here). */}
      <Route
        path="/auth/confirmed"
        element={
          <Suspense fallback={<PageFallback />}>
            <AuthConfirmed />
          </Suspense>
        }
      />

      {/* OS-1 family-circle invitation accept — public; token IS the credential */}
      <Route
        path="/family-circle/accept/:token"
        element={
          <Suspense fallback={<PageFallback />}>
            <AcceptGuardianInvite />
          </Suspense>
        }
      />

      {/*
        Announcement unsubscribe; public; the SIGNED token IS the credential.
        It ASKS before acting: mail clients and security scanners prefetch
        links, so a page that unsubscribed on mount would remove people who
        never clicked, and they would never find out. (The SMS page below does
        the same since KAN-215.)
      */}
      <Route
        path="/unsubscribe/:token"
        element={
          <Suspense fallback={<PageFallback />}>
            <EmailUnsubscribe />
          </Suspense>
        }
      />

      {/*
        Reminder RSVP link; public; the SIGNED token IS the credential.
        Unlike EmailUnsubscribe this page submits on load WHEN the link
        carries an answer (?a=yes|no|maybe) — those come from the reminder's
        own Yes/No/Maybe buttons, so the tap already WAS the decision. A bare
        link with no answer shows the three buttons instead.
      */}
      <Route
        path="/rsvp/:token"
        element={
          <Suspense fallback={<PageFallback />}>
            <RsvpFromLink />
          </Suspense>
        }
      />

      {/*
        How Kantera texts people — public, no token. The page a carrier or
        Twilio reviewer is sent to as proof of how consent is collected.
      */}
      <Route
        path="/sms-opt-in"
        element={
          <Suspense fallback={<PageFallback />}>
            <SmsOptIn />
          </Suspense>
        }
      />

      {/*
        CB-001 — SMS digest STOP link; public; token IS the credential. Every
        digest ends "Stop: <WEB_URL>/sms/unsubscribe/<token>". The page asks
        "Stop these texts?" and stops them only when its button is pressed:
        phones and carriers fetch texted links too (KAN-215).
      */}
      <Route
        path="/sms/unsubscribe/:token"
        element={
          <Suspense fallback={<PageFallback />}>
            <SmsUnsubscribe />
          </Suspense>
        }
      />

      {/*
        Practice check-in kiosk; public. The kiosk token IS the credential —
        paired once via a short code and held in localStorage, never in the URL.
        Must stay OUTSIDE ProtectedRoute and outside the Layout shell: there is
        no user, and the iPad would otherwise bounce to /login at the field.
      */}
      <Route
        path="/kiosk"
        element={
          <Suspense fallback={<PageFallback />}>
            <PracticeKiosk />
          </Suspense>
        }
      />
      <Route
        path="/kiosk/others"
        element={
          <Suspense fallback={<PageFallback />}>
            <PracticeKioskOthers />
          </Suspense>
        }
      />

      {/*
        `/` — marketing page when signed out, Dashboard in the shell when signed
        in. Listed BEFORE `/*` and matched ahead of it because React Router ranks
        an exact path above a splat; the Dashboard route that used to live inside
        the `/*` block moved here so the two never compete.
      */}
      <Route
        path="/"
        element={
          <RootRoute
            signedIn={
              <ProfileViewProvider>
                <Layout sidebar={<Sidebar />} topBar={<TopBar />} labels={layoutLabels}>
                  <SystemStatusBanner />
                  <ProfileCompletionGate>
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  </ProfileCompletionGate>
                </Layout>
              </ProfileViewProvider>
            }
          />
        }
      />

      {/* Protected Routes - wrapped with Layout and Sidebar */}
      <Route
        path="/*"
        element={
          <ProfileViewProvider>
            <Layout sidebar={<Sidebar />} topBar={<TopBar />} labels={layoutLabels}>
              <SystemStatusBanner />
              <ProfileCompletionGate>
                <Routes>
                  <Route
                    path="/players"
                    element={
                      <ProtectedRoute>
                        <Players />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/players/new"
                    element={
                      <ProtectedRoute>
                        <PlayerCreate />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/players/:id"
                    element={
                      <ProtectedRoute>
                        <PlayerDetail />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/players/:playerId/avatar-studio"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <AvatarStudio />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teams/compare"
                    element={
                      <ProtectedRoute>
                        <TeamComparison />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teams/new"
                    element={
                      <ProtectedRoute>
                        <TeamCreate />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teams/:id"
                    element={
                      <ProtectedRoute>
                        <TeamDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* KI-030 Carpool Coordinator (SCREEN_SPEC §6.5) */}
                  <Route
                    path="/teams/:teamId/carpool/:eventId"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <CarpoolCoordinator />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teams"
                    element={
                      <ProtectedRoute>
                        <Teams />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/matches/new"
                    element={
                      <ProtectedRoute>
                        <MatchCreate />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/matches/:id"
                    element={
                      <ProtectedRoute>
                        <MatchDetail />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/matches"
                    element={
                      <ProtectedRoute>
                        <Matches />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/seasons"
                    element={
                      <ProtectedRoute>
                        <Seasons />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/leagues"
                    element={
                      <ProtectedRoute>
                        <Leagues />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/standings"
                    element={
                      <ProtectedRoute>
                        <Standings />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/leagues/:id"
                    element={
                      <ProtectedRoute>
                        <LeagueDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* KI-012 B6 — League Scheduling (admin/DOC only) */}
                  <Route
                    path="/league-scheduling"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <LeagueScheduling />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/*
        Bulk add — upload → preview → commit → invite. Admin-only: an import
        creates players, teams and parent invitations. Every endpoint behind it
        is gated by bulk_add_enabled, which is OFF until migration 0124 is
        applied, and the page says so rather than showing a generic failure.
      */}
                  <Route
                    path="/bulk-add"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'owner']}>
                        <Suspense fallback={<PageFallback />}>
                          <BulkAdd />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/import"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Import />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/formation"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Formation />
                      </ProtectedRoute>
                    }
                  />

                  {/* Tactical Animation Engine */}
                  <Route
                    path="/tactics"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <TacticsLibrary />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tactics/formations"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <FormationEncyclopedia />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/leaderboard"
                    element={
                      <ProtectedRoute>
                        <Leaderboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/gamification"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Gamification />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* KI-010 Player Portal — 13+ self-service. PlayerSelfRoute fail-closes for U13. */}
                  <Route
                    path="/my-profile"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MyProfile />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/my-avatar"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MyAvatarPage />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/my-schedule"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MySchedule />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/referee"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <RefereeDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/assignments/:matchId/checkin"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <PlayerCheckin />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/incidents"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <IncidentLog />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager"
                    element={
                      <ProtectedRoute requiredRole={['team_manager', 'coach', 'admin', 'owner']}>
                        <Suspense fallback={<PageFallback />}>
                          <TeamManagerDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/comms"
                    element={
                      <ProtectedRoute requiredRole={['team_manager', 'coach', 'admin', 'owner']}>
                        <Suspense fallback={<PageFallback />}>
                          <TeamManagerComms />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/volunteers"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <TeamManagerVolunteers />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-team"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MyTeam />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/my-achievements"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Achievements />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/my-homework"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MyHomework />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />
                  <Route
                    path="/challenges"
                    element={
                      <PlayerSelfRoute>
                        <Suspense fallback={<PageFallback />}>
                          <SkillChallenges />
                        </Suspense>
                      </PlayerSelfRoute>
                    }
                  />

                  {/* Wave 7: Communication, Registration & Settings */}
                  <Route
                    path="/messages/direct"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <DirectMessages />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <NotificationCenter />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/registration/forms/:formId/edit"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <RegistrationFormBuilder />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/registration/forms"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <RegistrationFormBuilder />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/registration/manage"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <RegistrationManagement />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/appearance"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <AppearanceSettings />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Wave 8: Tournaments, Archive, Search */}
                  <Route
                    path="/tournaments"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <TournamentManager />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/season-archive"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <SeasonArchive />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/season-archive/:id"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <SeasonDetail />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <SearchPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Wave 9: Tactical Animation Engine */}
                  <Route
                    path="/tactics/editor"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <TacticsEditor />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matches/:id/game-plan"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <GameDayPrep />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matches/:id/film"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MatchFilm />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/training"
                    element={
                      <ProtectedRoute>
                        <TrainingSessions />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/training/:teamId/:sessionId"
                    element={
                      <ProtectedRoute>
                        <TrainingSessionDetail />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/consent"
                    element={
                      // Parent-facing COPPA consent dashboard (sidebar nav is PARENT_ONLY). Admin kept
                      // so the Compliance page's "Resolve" deep-link still reaches it.
                      <ProtectedRoute requiredRole={['admin', 'parent']}>
                        <ConsentManagement />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <Analytics />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/compliance"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <Compliance />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/compliance/withdrawn-media"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <WithdrawnMedia />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clubs"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Clubs />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clubs/:id"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <ClubDetail />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/clubs/:id/manage"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <ClubManagement />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <Billing />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/safeguarding"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <Safeguarding />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />

                  {/* D-002: Tryouts Workflow */}
                  <Route
                    path="/tryouts"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <Tryouts />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Development */}
                  <Route
                    path="/skill-assessments"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <SkillAssessments />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/training-plans"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <TrainingPlans />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/player-development"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <PlayerDevelopment />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Reports />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Media */}
                  <Route
                    path="/video-analysis"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <VideoAnalysis />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/highlights"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <Highlights />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/photos"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <PhotoGallery />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Communication */}
                  <Route
                    path="/messaging"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Messaging />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/coach-channel"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <CoachChannel />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/staff"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <StaffManagement />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/email-deliveries"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <EmailDeliveries />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/erasure-requests"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <ErasureRequests />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/venues"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Venues />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Wave 2: Match day operations */}
                  <Route
                    path="/matches/:id/lineup"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <LineupBuilder />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matches/:id/live"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <LiveScoring />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matches/:id/report"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <PostMatchReport />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* D-009: Shareable result card (legacy SVG card) */}
                  <Route
                    path="/matches/:id/share"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MatchShareCard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* KI-012 B7 — Scouting Reports */}
                  <Route
                    path="/scouting-reports"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <ScoutingReports />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* KI-012 B8 — Game Strategy */}
                  <Route
                    path="/matches/:id/game-strategy"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <GameStrategy />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Wave 3: Parent portal */}
                  <Route
                    path="/matches/:id/rsvps"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <MatchRsvps />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/development-report"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <ParentDevelopmentReport />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Education */}
                  <Route
                    path="/courses"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <Courses />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/courses/:courseId"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <CourseDetail />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/nutrition"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <NutritionHub />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Family — parent-role only */}
                  <Route
                    path="/my-children"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <ParentChildManagement />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* OS-1 multi-guardian dashboard */}
                  <Route
                    path="/family-circle"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <FamilyCircle />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* GF/Grandparent extended-family read-only portal */}
                  <Route
                    path="/family/view"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <FamilyDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/family/schedule"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <FamilySchedule />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/family/standings"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <FamilyStandings />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/family/badges"
                    element={
                      <ProtectedRoute requiredRole="parent">
                        <Suspense fallback={<PageFallback />}>
                          <FamilyBadges />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Help */}
                  <Route
                    path="/help-center"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <HelpCenter />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Platform Admin */}
                  <Route
                    path="/platform"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/invite"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformInviteClubAdmin />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/waitlist"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformWaitlist />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/clubs"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/clubs/new"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformAddClub />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/growth"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/revenue"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/usage"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <PlatformDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Platform Admin — Wave 10 P3 tools */}
                  <Route
                    path="/platform/clubs/:clubId"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <ClubDeepDive />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/analytics"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <ProductAnalytics />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/engineering"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <EngineeringDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/marketing"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <MarketingDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/finance"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <FinanceDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/support"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <SupportDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/content"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <ContentManagement />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform/cost-model"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <CostModel />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* Role-gated deliberately. The other /platform/* routes are bare
                      ProtectedRoutes (queued as a separate fix); a screen that flips
                      global kill switches does not wait for that. */}
                  <Route
                    path="/platform/feature-flags"
                    element={
                      <ProtectedRoute requiredRole={PLATFORM_ROLES}>
                        <Suspense fallback={<PageFallback />}>
                          <FeatureFlags />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/videos/:videoId/review"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <VideoReview />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Club Onboarding */}
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <ClubOnboarding />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Club Brand Kit — onboarding step 2 of 4 (standalone, no sidebar) */}
                  <Route
                    path="/onboarding-club-brand"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <Suspense fallback={<PageFallback />}>
                          <OnboardingClubBrand />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Coach "join a team" onboarding (standalone, no sidebar). No
                  requiredRole — a coach joining a club is not an admin. */}
                  <Route
                    path="/onboarding-coach"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <OnboardingCoach />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Player "join your team" onboarding (standalone, no sidebar).
                  No requiredRole — a player joining is not an admin. */}
                  <Route
                    path="/onboarding-player"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <OnboardingPlayer />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Parent "add your child" onboarding (standalone, no sidebar).
                  No requiredRole — a parent adding a child is not an admin. */}
                  <Route
                    path="/onboarding-parent"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageFallback />}>
                          <OnboardingParent />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* D-004 Discovery (flag-gated, default OFF — pages render a notice when flag is OFF) */}
                  <Route
                    path="/discovery/feed"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <DiscoveryFeed />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/discovery/profile/:playerId"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <DiscoveryProfile />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/discovery/controls"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'parent']}>
                        <Suspense fallback={<PageFallback />}>
                          <DiscoveryControls />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* D-005 Comparison (flag-gated, default OFF — pages render a notice when flag is OFF) */}
                  <Route
                    path="/comparison"
                    element={
                      <ProtectedRoute requiredRole={['admin', 'coach']}>
                        <Suspense fallback={<PageFallback />}>
                          <PlayerComparison />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* Public read-only shared comparison — no auth required */}
                  <Route
                    path="/comparison/shared/:token"
                    element={
                      <Suspense fallback={<PageFallback />}>
                        <SharedComparison />
                      </Suspense>
                    }
                  />

                  {/* Fallback */}
                  <Route path="/404" element={<NotFound />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </ProfileCompletionGate>
            </Layout>
          </ProfileViewProvider>
        }
      />
    </Routes>
  );
};

const DENSITY_STORAGE_KEY = 'k_density';

function readStoredDensity(): Density {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored === 'compact' || stored === 'default' || stored === 'comfortable') return stored;
  } catch {
    // ignore
  }
  return 'default';
}

export const App: React.FC = () => {
  const [density, setDensity] = useState<Density>(readStoredDensity);

  // Listen for storage events so AppearanceSettings can update density
  // by writing to localStorage and dispatching a custom event.
  useEffect(() => {
    const handleDensityChange = () => {
      setDensity(readStoredDensity());
    };
    window.addEventListener('k-density-change', handleDensityChange);
    return () => window.removeEventListener('k-density-change', handleDensityChange);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultMode="system">
        <DensityProvider density={density}>
          <AuthProvider>
            <UserProfileProvider>
              <UploadProvider>
                <I18nProvider>
                  <ToastProvider>
                    <EnvBanner />
                    <div style={{ paddingTop: ENV_BANNER_HEIGHT }}>
                      <AppRoutes />
                      <AppShell />
                      <UploadIndicator />
                    </div>
                  </ToastProvider>
                </I18nProvider>
              </UploadProvider>
            </UserProfileProvider>
          </AuthProvider>
        </DensityProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
